import { useState } from 'react';
import { colors } from 'ui/theme/colors';
import { HelpTooltip } from 'ui/tooltip/Variants';

import { RUMHelpers } from 'services/observability/datadog';
import { BUTTON_SIZES } from 'ui/button';
import { View16 } from 'ui/icons/aurora/16px/View16';
import { SlideOver } from 'ui/slideOver/SlideOver';
import {
  TEST_IDS,
  XAI_CONTRIBUTING_EVENTS_AND_USER_FIELDS,
  XAI_HEADER_TILE_TEXT,
  XAI_LEFT_NAV_SLIDEOVER_BUTTON_TEXT_CONTENT,
  XAI_MOST_MEANINGFUL,
  XAI_TOOLTIP,
  XAI_TOP_FEATURES_EMPTY_STATE_TEXT_CONTENT,
} from '../../constants';
import { createActionName } from '../../observabilityUtils';
import { PredictiveAudienceGoal } from '../../types';
import {
  MarginlessBoldSubtitle2,
  XaiContributingFeatures,
  XaiLeftCard,
  XaiLeftCardContent,
  XaiLeftCardContentExploreButton,
  XaiLeftCardHeader,
  XaiLeftNavTopFeaturesContainer,
  XaiMonospaceText,
  XaiMostMeaningfulHeaderText,
  XaiTopFeaturesEmptyStateText,
  XaiTotalCounts,
} from '../Styled';
import { XaiMonospaceTextTooltipWrapper } from './XaiMonospaceTextTooltipWrapper';
import { XaiSlideOverContent } from './XaiSlideOverContent';
import { XaiSlideOverHeader } from './XaiSlideOverHeader';

type XAILeftNavCardTypes = {
  goal: PredictiveAudienceGoal;
};

export const XAILeftNavCard = ({ goal }: XAILeftNavCardTypes) => {
  const [isIsXAIDrawerShown, setIsXAIDrawerShown] = useState<boolean>(false);
  const { modelDetails } = goal;
  const explainableFeatures = modelDetails?.explainableFeatures;
  const features = explainableFeatures?.features || [];
  const featuresCount = features.length;
  const topThreeFeaturesNames = features.slice(0, 3).map((feature) => feature.name);
  const predictiveStrength = modelDetails?.model_quality || '';

  const openXAISlideout = () => {
    RUMHelpers.addRUMAction(createActionName('Open XAI'));
    setIsXAIDrawerShown((prev) => !prev);
  };

  const closeXaiSlideout = () => {
    setIsXAIDrawerShown(false);
  };

  return (
    <XaiLeftCard data-test={`${TEST_IDS.XAILeftNavCard}`}>
      <XaiLeftCardHeader>{XAI_HEADER_TILE_TEXT}</XaiLeftCardHeader>
      <XaiLeftCardContent>
        {featuresCount > 0 ? (
          <XaiContributingFeatures data-test={`${TEST_IDS.XAIContributingFeatures}`}>
            <XaiTotalCounts>{`${featuresCount} ${XAI_CONTRIBUTING_EVENTS_AND_USER_FIELDS}`}</XaiTotalCounts>
            <XaiMostMeaningfulHeaderText>
              <MarginlessBoldSubtitle2>{XAI_MOST_MEANINGFUL}</MarginlessBoldSubtitle2>
              <HelpTooltip
                tooltipContent={XAI_TOOLTIP}
                iconProps={{ color: colors.secondaryText }}
              />
            </XaiMostMeaningfulHeaderText>
            <XaiLeftNavTopFeaturesContainer>
              {topThreeFeaturesNames.map((name) => {
                return (
                  <XaiMonospaceTextTooltipWrapper key={name} name={name} $textMaxWidth={'170'}>
                    <XaiMonospaceText data-test={`${TEST_IDS.XAITopThreeFeatures}_${name}`}>
                      {name}
                    </XaiMonospaceText>
                  </XaiMonospaceTextTooltipWrapper>
                );
              })}
            </XaiLeftNavTopFeaturesContainer>
          </XaiContributingFeatures>
        ) : (
          <XaiTopFeaturesEmptyStateText>
            {XAI_TOP_FEATURES_EMPTY_STATE_TEXT_CONTENT}
          </XaiTopFeaturesEmptyStateText>
        )}
        <XaiLeftCardContentExploreButton
          size={BUTTON_SIZES.SMALL}
          onClick={openXAISlideout}
          data-test={`${TEST_IDS.XAILeftNavViewBtn}`}
          isDisabled={featuresCount === 0}
        >
          <View16 />
          {XAI_LEFT_NAV_SLIDEOVER_BUTTON_TEXT_CONTENT}
        </XaiLeftCardContentExploreButton>
      </XaiLeftCardContent>
      <SlideOver
        id="xai"
        isOpen={isIsXAIDrawerShown}
        onClose={closeXaiSlideout}
        headerContent={<XaiSlideOverHeader />}
        bodyContent={
          <XaiSlideOverContent features={features} predictiveStrength={predictiveStrength} />
        }
        onClickOutside={closeXaiSlideout}
      />
    </XaiLeftCard>
  );
};
